import { useMemo, useState, type ReactNode } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  EMBER,
  GLASS_CARD_STYLE,
  HEADER_INSET,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import {
  HomepageReviewConceptHome,
  PostCycleHandoffCard,
  ConceptSectionView,
  todayRows,
  betaMetrics,
  betaWaitResultPlanCards,
  betaWaitWhatToKnowCards,
  eggLabUpdateCards,
  type HomepageReviewConceptVariant,
  type Widget,
  type Task,
  type TimelineItem,
  type DecisionOption,
} from "@src/packages/homepage/plan/components/HomepageReviewConceptHome";
import { resolvePostCycleHandoffJourney } from "@src/packages/homepage/plan/postCycleHandoff";
import {
  GuidedPreCycleConceptHome,
  type GuidedPreCycleConceptVariant,
} from "@src/packages/homepage/plan/components/GuidedPreCycleConceptHome";
import OvarianStimulationMonitoringSection from "./care/sections/OvarianStimulationMonitoringSection";
import HelperJourneyHero from "../components/HelperJourneyHero";
import {
  FreezingResultsFunnel,
  buildEmbryoFreezingFunnel,
} from "@src/packages/care/cryovault/FreezingResultsFunnel";
import { PreCycleEggFreezingHome } from "@src/packages/homepage/plan/components/PreCycleEggFreezingHome";
import { PreCycleEmbryoFreezingHome } from "@src/packages/homepage/plan/components/PreCycleEmbryoFreezingHome";
import { PreCycleSpermFreezingHome } from "@src/packages/homepage/plan/components/PreCycleSpermFreezingHome";
import { useStartTreatmentController } from "../StartTreatmentController";
import ChatHubScreen from "./chat/ChatHubScreen";
import {
  getJourneyForState,
  type JourneyDefinition,
  type Pathway,
  type PlanAction,
  type StepStatus,
} from "@src/packages/homepage/plan";
import type {
  HomepageGender,
  HomepageGoal,
} from "@src/packages/homepage/homepage.types";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type GuidedPreCycleSurfaceId =
  | "egg-freezing-precycle-guided"
  | "embryo-freezing-precycle-guided"
  | "sperm-freezing-precycle-guided";

type GuidedPreCycleSurface = {
  id: GuidedPreCycleSurfaceId;
  kind: "guided-precycle";
  label: string;
  group: string;
  journey: {
    gender: HomepageGender;
    goal: HomepageGoal;
    pathway: Pathway;
  };
};

type ConceptSurface = {
  id: HomepageReviewConceptVariant;
  kind: "concept";
  label: string;
  group: string;
};

type GuidedConceptSurface = {
  id: string;
  kind: "guided-concept";
  variant: GuidedPreCycleConceptVariant;
  viewerSexAtBirth?: "AFAB" | "AMAB" | "unknown";
  label: string;
  group: string;
};

type HelperSurface = {
  id: string;
  kind: "helper";
  role: "donor" | "carrier";
  label: string;
  group: string;
};

type ComponentBehavior = "wire-empty" | "wire-hidden" | "static" | "estimate";

type ComponentSurface = {
  id: string;
  kind: "component";
  label: string;
  group: string;
  behavior: ComponentBehavior;
  rule: string;
  filled?: Widget;
  empty?: Widget;
  /** Renders a real component for the filled/empty state instead of a fixture
   *  widget, for widgets that will reuse an existing data-connected component. */
  filledRender?: () => ReactNode;
  emptyRender?: () => ReactNode;
  note?: string;
};

type CanvasSurface =
  | GuidedPreCycleSurface
  | GuidedConceptSurface
  | ConceptSurface
  | ComponentSurface
  | HelperSurface;

const BEHAVIOR_LABELS: Record<ComponentBehavior, string> = {
  "wire-empty": "Wire to real data, keep visible with an empty state",
  "wire-hidden": "Wire to real data, hidden when there is no data",
  static: "Static by design",
  estimate: "Modeled estimate by design",
};

const FIXTURE_TASKS: Task[] = [
  {
    title: "Confirm consent forms",
    detail: "Signature pending in the clinic portal",
    owner: "You",
    due: "Today",
    status: "now",
  },
  {
    title: "Pharmacy pickup",
    detail: "Refrigerated medications ready for pickup",
    owner: "You",
    due: "Tomorrow",
    status: "waiting",
  },
  {
    title: "Baseline bloodwork",
    detail: "Completed at intake",
    owner: "Clinic",
    due: "Done",
    status: "done",
  },
];

const FIXTURE_TIMELINE: TimelineItem[] = [
  { label: "Baseline", detail: "Ultrasound and labs", status: "done" },
  {
    label: "Stimulation",
    detail: "Daily injections with monitoring visits",
    status: "now",
  },
  { label: "Trigger", detail: "Timed to lead follicles", status: "next" },
  { label: "Retrieval", detail: "Procedure day", status: "waiting" },
];

const FIXTURE_DECISION_OPTIONS: DecisionOption[] = [
  {
    title: "Continue current protocol",
    detail: "Response is tracking expectations",
    status: "Likely",
  },
  {
    title: "Adjust medication dose",
    detail: "If follicle growth slows",
    status: "Possible",
  },
  {
    title: "Pause and reassess",
    detail: "Only if monitoring flags a risk",
    status: "Rare",
  },
];

const COMPONENT_SURFACES: ComponentSurface[] = [
  {
    id: "component-today-list",
    kind: "component",
    label: "Today list",
    group: "Components",
    behavior: "wire-empty",
    rule: "Rows come from real medications and appointments. With nothing scheduled, keep the card visible with a calm empty state and offer a peek at tomorrow.",
    filled: { type: "today", title: "Today", rows: todayRows("stimulation") },
    empty: { type: "today", title: "Today", rows: [] },
  },
  {
    id: "component-hero-metrics",
    kind: "component",
    label: "Hero metrics",
    group: "Components",
    behavior: "wire-hidden",
    rule: "Pure display of timing metrics from real cycle phases. Hero metrics are not a contribution point, so an empty metric grid is useless: hide each metric until it has a value, and hide the whole block if none do.",
    filled: {
      type: "metrics",
      title: "Hero metrics",
      icon: "panel",
      metrics: betaMetrics,
    },
  },
  {
    id: "component-test-results",
    kind: "component",
    label: "Test results preview",
    group: "Components",
    behavior: "wire-hidden",
    rule: "Results come from the test results API. A user with no tests should not see this card at all.",
    filled: {
      type: "test-results",
      title: "Test results",
      icon: "panel",
      variant: "default",
    },
    note: "The empty state cannot be rendered yet: this component still resolves its own preview rows internally. Wiring will pass real rows in as props.",
  },
  {
    id: "component-monitoring-chart",
    kind: "component",
    label: "Monitoring chart",
    group: "Components",
    behavior: "wire-empty",
    rule: "Follicle, estradiol, and lining series come from real monitoring visits. During an active cycle it stays visible even with no data so the user can prep (the 'Add monitoring data' empty state); outside an active cycle it is hidden. Reuses the canonical cycle-detail monitoring section.",
    emptyRender: () => (
      <OvarianStimulationMonitoringSection
        cycleId={null}
        cycleStartDate={null}
        phases={[]}
        derivedStepProgress={[]}
        defaultView="table"
        titleMarginTop={0}
      />
    ),
    note: "Empty state shown is the canonical monitoring section used by the Home and cycle-detail routes.",
  },
  {
    id: "component-funnel",
    kind: "component",
    label: "Outcome funnel",
    group: "Components",
    behavior: "wire-hidden",
    rule: "Counts come from real specimens and the embryo report. When wired, this reuses the egg/embryo pipeline funnel (FreezingResultsFunnel) from the embryo report screen, which hides itself when there are no results.",
    filledRender: () => (
      <FreezingResultsFunnel
        stages={buildEmbryoFreezingFunnel({
          eggsCollected: 14,
          matureEggs: 11,
          fertilized: 8,
          blastocysts: 4,
          euploid: 2,
        })}
      />
    ),
    note: "Filled state shown is the real FreezingResultsFunnel from the embryo report screen (Care). It returns nothing when there are no results, which is the empty behavior.",
  },
  {
    id: "component-lab-carousel",
    kind: "component",
    label: "Lab update carousel",
    group: "Components",
    behavior: "wire-hidden",
    rule: "Read-only display of lab updates from real cycle test results. Not a contribution point on the homepage, so hide the whole card when there are none.",
    filled: {
      type: "lab-carousel",
      title: "Lab updates",
      icon: "panel",
      cards: eggLabUpdateCards,
    },
  },
  {
    id: "component-cycle-logistics",
    kind: "component",
    label: "Cycle logistics carousel",
    group: "Components",
    behavior: "static",
    rule: "Educational plans, like the beta result plan, stay static. Order and shipping status has no backend yet, so status variants stay hidden for launch.",
    filled: {
      type: "cycle-logistics",
      title: "Beta result plan",
      icon: "start",
      cards: betaWaitResultPlanCards,
    },
  },
  {
    id: "component-what-to-know",
    kind: "component",
    label: "What to know cards",
    group: "Components",
    behavior: "static",
    rule: "General education by design. No wiring or empty state needed.",
    filled: {
      type: "what-to-know",
      title: "What to know",
      icon: "panel",
      cards: betaWaitWhatToKnowCards,
    },
  },
  {
    id: "component-outcome-estimate",
    kind: "component",
    label: "Outcome estimate funnel",
    group: "Components",
    behavior: "estimate",
    rule: "A modeled projection by design. Stays visible without data, but must always read as an estimate, never as the user's results.",
    filled: {
      type: "outcome-estimate-funnel",
      title: "Outcome estimate",
      icon: "panel",
      variant: "egg-freezing",
    },
  },
  {
    id: "component-tasks",
    kind: "component",
    label: "Task list",
    group: "Components",
    behavior: "static",
    rule: "Instructional checklists can stay static. Hide any version that pretends to track real state it does not have.",
    filled: {
      type: "tasks",
      title: "Prep tasks",
      icon: "panel",
      tasks: FIXTURE_TASKS,
    },
  },
  {
    id: "component-timeline",
    kind: "component",
    label: "Timeline",
    group: "Components",
    behavior: "static",
    rule: "Process education stays static, but strip fake dates. Real dates come from cycle phases once wired.",
    filled: {
      type: "timeline",
      title: "Cycle timeline",
      icon: "panel",
      items: FIXTURE_TIMELINE,
    },
  },
  {
    id: "component-decision",
    kind: "component",
    label: "Decision view",
    group: "Components",
    behavior: "static",
    rule: "Educational branch explainer. Stays static.",
    filled: {
      type: "decision",
      title: "What could change",
      icon: "panel",
      options: FIXTURE_DECISION_OPTIONS,
    },
  },
];

const SURFACES: CanvasSurface[] = [
  ...COMPONENT_SURFACES,
  {
    id: "egg-freezing-precycle-guided",
    kind: "guided-precycle",
    label: "Egg freezing pre-cycle",
    group: "Guided pre-cycle",
    journey: { gender: "female", goal: "preserve", pathway: "solo" },
  },
  {
    id: "embryo-freezing-precycle-guided",
    kind: "guided-precycle",
    label: "Embryo freezing pre-cycle",
    group: "Guided pre-cycle",
    journey: {
      gender: "female",
      goal: "embryo-preserve",
      pathway: "with-partner",
    },
  },
  {
    id: "sperm-freezing-precycle-guided",
    kind: "guided-precycle",
    label: "Sperm freezing pre-cycle",
    group: "Guided pre-cycle",
    journey: { gender: "male", goal: "preserve", pathway: "solo" },
  },
  {
    id: "setup",
    variant: "setup",
    kind: "guided-concept",
    label: "Setup",
    group: "Guided entry",
  },
  {
    id: "learn-explorer",
    variant: "learn-explorer",
    kind: "guided-concept",
    label: "Learn",
    group: "Guided entry",
  },
  {
    id: "ivf-precycle",
    variant: "ivf-precycle",
    viewerSexAtBirth: "AFAB",
    kind: "guided-concept",
    label: "IVF pre-cycle",
    group: "Guided grow",
  },
  {
    id: "ivf-precycle-sperm-first",
    variant: "ivf-precycle",
    viewerSexAtBirth: "AMAB",
    kind: "guided-concept",
    label: "IVF pre-cycle (sperm-first)",
    group: "Guided grow",
  },
  {
    id: "iui-precycle",
    variant: "iui-precycle",
    kind: "guided-concept",
    label: "IUI pre-cycle",
    group: "Guided grow",
  },
  {
    id: "egg-freezing-active",
    kind: "concept",
    label: "Egg freezing active",
    group: "Cycle concepts",
  },
  {
    id: "embryo-freezing-active",
    kind: "concept",
    label: "Embryo freezing active",
    group: "Cycle concepts",
  },
  {
    id: "sperm-freezing-active",
    kind: "concept",
    label: "Sperm freezing active",
    group: "Cycle concepts",
  },
  {
    id: "ivf-active",
    kind: "concept",
    label: "IVF active",
    group: "Cycle concepts",
  },
  {
    id: "iui-active",
    kind: "concept",
    label: "IUI active",
    group: "Cycle concepts",
  },
  {
    id: "beta-wait",
    kind: "concept",
    label: "Beta wait",
    group: "Wait concepts",
  },
  {
    id: "iui-wait",
    kind: "concept",
    label: "IUI wait",
    group: "Wait concepts",
  },
  {
    id: "oi-wait",
    kind: "concept",
    label: "OI wait",
    group: "Wait concepts",
  },
  {
    id: "sperm-freezing-wait",
    kind: "concept",
    label: "Sperm results wait",
    group: "Wait concepts",
  },
  {
    id: "ivf-positive",
    kind: "concept",
    label: "IVF positive",
    group: "Results concepts",
  },
  {
    id: "ivf-negative",
    kind: "concept",
    label: "IVF negative",
    group: "Results concepts",
  },
  {
    id: "egg-freezing-postcycle",
    kind: "concept",
    label: "Egg freezing post-cycle",
    group: "Post-cycle concepts",
  },
  {
    id: "embryo-freezing-postcycle",
    kind: "concept",
    label: "Embryo freezing post-cycle",
    group: "Post-cycle concepts",
  },
  {
    id: "sperm-freezing-postcycle",
    kind: "concept",
    label: "Sperm freezing post-cycle",
    group: "Post-cycle concepts",
  },
  {
    id: "donor",
    kind: "helper",
    role: "donor",
    label: "Donor",
    group: "Helper",
  },
  {
    id: "carrier",
    kind: "helper",
    role: "carrier",
    label: "Carrier",
    group: "Helper",
  },
  {
    id: "intended-parent",
    kind: "concept",
    label: "Intended parent",
    group: "Helper",
  },
];

const GROUPS = [...new Set(SURFACES.map((surface) => surface.group))];
const DEFAULT_SURFACE: CanvasSurface = SURFACES[0]!;

function getInitialSurfaceId(): CanvasSurface["id"] {
  if (typeof window === "undefined") return "embryo-freezing-precycle-guided";

  try {
    const params = new URLSearchParams(window.location.search);
    const requestedSurface = params.get("surface") ?? params.get("dev_surface");

    return (
      SURFACES.find((surface) => surface.id === requestedSurface)?.id ??
      "embryo-freezing-precycle-guided"
    );
  } catch {
    return "embryo-freezing-precycle-guided";
  }
}

function getGuidedJourney(surface: GuidedPreCycleSurface): JourneyDefinition {
  return getJourneyForState(
    surface.journey.gender,
    surface.journey.goal,
    surface.journey.pathway,
  );
}

function getPrepSteps(journey: JourneyDefinition) {
  return journey.steps.map((step) => ({
    id: step.id,
    title: step.title,
    icon: step.icon,
    status: "not-started" as StepStatus,
  }));
}

export default function HomepageCanvasScreen() {
  const { openStartTreatment } = useStartTreatmentController();
  const capturePress = usePressCapture("HomepageCanvasScreen");
  const [selectedSurfaceId, setSelectedSurfaceId] = useState<
    CanvasSurface["id"]
  >(() => getInitialSurfaceId());
  const [lastAction, setLastAction] = useState<PlanAction | null>(null);
  const [chatAction, setChatAction] = useState<PlanAction | null>(null);
  const selected = useMemo(
    () =>
      SURFACES.find((surface) => surface.id === selectedSurfaceId) ??
      DEFAULT_SURFACE,
    [selectedSurfaceId],
  );

  const handleAction = (action: PlanAction) => {
    setLastAction(action);
    if (action.signal === "open-start-treatment-modal") {
      openStartTreatment({
        gender:
          action.params?.presetCycleType === "sperm_freezing"
            ? "male"
            : "female",
        presetCycleType: action.params?.presetCycleType ?? null,
        source: "HomepageCanvasScreen",
      });
      return;
    }
    if (action.signal === "open-ai-chat") {
      setChatAction(action);
    }
  };

  return (
    <GlassScreenLayout>
      <View
        testID="canvas-last-action"
        accessibilityLabel={
          lastAction ? JSON.stringify(lastAction) : "No action selected"
        }
        style={{ position: "absolute", width: 1, height: 1, opacity: 0 }}
      />
      <View
        style={{
          flex: 1,
          flexDirection: "row",
          paddingTop: SCREEN_PADDING_TOP || HEADER_INSET,
          paddingHorizontal: 16,
          paddingBottom: 24,
          gap: 16,
        }}
      >
        <ScrollView
          testID="canvas-nav"
          style={{ width: 260, flexShrink: 0 }}
          contentContainerStyle={{ paddingBottom: 48 }}
          showsVerticalScrollIndicator={false}
        >
          <Text
            accessibilityRole="header"
            testID="canvas-title"
            className="font-dm-sans"
            style={{
              fontSize: 22,
              fontWeight: "700",
              color: EMBER.colors.text,
              marginBottom: 4,
            }}
          >
            Homepage Canvas
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              lineHeight: 18,
              color: EMBER.colors.textMid,
              marginBottom: 18,
            }}
          >
            Redesigned homepage surfaces
          </Text>

          {GROUPS.map((group) => (
            <View key={group} style={{ marginBottom: 18 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  fontWeight: "700",
                  color: EMBER.colors.textFaint,
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                {group}
              </Text>
              <View style={{ gap: 8 }}>
                {SURFACES.filter((surface) => surface.group === group).map(
                  (surface) => {
                    const isSelected = surface.id === selectedSurfaceId;
                    return (
                      <Pressable
                        key={surface.id}
                        accessibilityRole="button"
                        accessibilityLabel={surface.label}
                        accessibilityState={{ selected: isSelected }}
                        aria-current={isSelected ? "true" : undefined}
                        testID={`canvas-surface-${surface.id}`}
                        onPress={() => {
                          capturePress(
                            pressLabel(surface.label),
                            `canvas_surface_${surface.id}`,
                          );
                          setSelectedSurfaceId(surface.id);
                        }}
                        style={({ pressed }) => ({
                          ...(GLASS_CARD_STYLE as object),
                          borderRadius: 8,
                          paddingVertical: 10,
                          paddingHorizontal: 12,
                          backgroundColor: isSelected
                            ? "rgba(255,255,255,0.82)"
                            : pressed
                              ? "rgba(255,255,255,0.64)"
                              : "rgba(255,255,255,0.48)",
                          borderColor: isSelected
                            ? "rgba(99,67,42,0.34)"
                            : "rgba(255,255,255,0.58)",
                        })}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            lineHeight: 18,
                            fontWeight: isSelected ? "700" : "600",
                            color: EMBER.colors.text,
                          }}
                        >
                          {surface.label}
                        </Text>
                      </Pressable>
                    );
                  },
                )}
              </View>
            </View>
          ))}
        </ScrollView>

        <ScrollView
          testID="canvas-content"
          style={{ flex: 1 }}
          contentContainerStyle={{
            maxWidth: 760,
            width: "100%",
            alignSelf: "center",
            paddingBottom: 96,
          }}
          showsVerticalScrollIndicator={false}
        >
          <View style={{ marginBottom: 12, paddingHorizontal: 2 }}>
            <Text
              accessibilityRole="header"
              testID="canvas-selected-label"
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "700",
                color: EMBER.colors.textFaint,
              }}
            >
              {selected.label}
            </Text>
          </View>
          {selected.kind === "component" ? (
            <ComponentStatesPreview
              surface={selected}
              onAction={handleAction}
            />
          ) : selected.kind === "guided-precycle" ? (
            <GuidedPreCyclePreview
              surface={selected}
              onAction={handleAction}
              onOpenPlan={handleAction}
            />
          ) : selected.kind === "guided-concept" ? (
            <GuidedPreCycleConceptHome
              variant={selected.variant}
              userAge={34}
              viewerSexAtBirth={selected.viewerSexAtBirth}
              onAction={handleAction}
              onOpenPlan={() =>
                handleAction({ label: "Open plan", screen: "PlanBuilder" })
              }
              onEditPlan={() =>
                handleAction({ label: "Edit plan", screen: "PlanBuilder" })
              }
            />
          ) : selected.kind === "helper" ? (
            <HelperJourneyHero role={selected.role} />
          ) : (
            <HomepageReviewConceptHome
              variant={selected.id}
              onAction={handleAction}
              /* The post-cycle closing card is no longer a widget in the
                 variant's list — it has to be the last block of the page, and a
                 list renders above the records section `PlanHomeScreen` appends
                 after this component. This canvas has no records section, but it
                 renders the same variants, so it supplies the card the same way
                 that screen does; without this the three post-cycle surfaces
                 lost it entirely. */
              endOfPageSlot={(() => {
                const journey = resolvePostCycleHandoffJourney(selected.id);
                return journey ? (
                  <PostCycleHandoffCard
                    journey={journey}
                    onAction={handleAction}
                  />
                ) : undefined;
              })()}
            />
          )}
        </ScrollView>
      </View>
      {chatAction?.signal === "open-ai-chat" ? (
        <View
          testID="canvas-action-destination"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 20,
            backgroundColor: "rgba(247,242,236,0.98)",
          }}
        >
          <ChatHubScreen
            onClose={() => setChatAction(null)}
            route={{
              params: {
                initialPrompt: chatAction.params?.initialPrompt,
                // Match the production handoff: the chat opens with the prompt
                // already sent so the assistant starts responding immediately.
                autoSendInitialPrompt: true,
              },
            }}
          />
        </View>
      ) : null}
    </GlassScreenLayout>
  );
}

function GuidedPreCyclePreview({
  surface,
  onAction,
  onOpenPlan,
}: {
  surface: GuidedPreCycleSurface;
  onAction: (action: PlanAction) => void;
  onOpenPlan: (action: PlanAction) => void;
}) {
  const journey = getGuidedJourney(surface);
  const steps = getPrepSteps(journey);
  const commonProps = {
    activeStep: null,
    steps,
    completedSteps: 0,
    totalSteps: journey.steps.length,
    userAge: 34,
    onAction,
    onOpenPlan: () => onOpenPlan({ label: "Open plan", screen: "PlanBuilder" }),
    onEditPlan: () => onAction({ label: "Edit plan", screen: "PlanBuilder" }),
  };

  if (surface.id === "egg-freezing-precycle-guided") {
    return <PreCycleEggFreezingHome {...commonProps} />;
  }
  if (surface.id === "embryo-freezing-precycle-guided") {
    return <PreCycleEmbryoFreezingHome {...commonProps} />;
  }
  return <PreCycleSpermFreezingHome {...commonProps} />;
}

function HiddenWhenEmptyIndicator() {
  return (
    <View
      style={{
        borderRadius: 12,
        borderWidth: 1,
        borderStyle: "dashed",
        borderColor: "rgba(99,67,42,0.28)",
        paddingVertical: 14,
        paddingHorizontal: 14,
        backgroundColor: "rgba(255,255,255,0.28)",
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 18,
          color: EMBER.colors.textMid,
          fontStyle: "italic",
        }}
      >
        Not rendered. With no data this component is hidden entirely.
      </Text>
    </View>
  );
}

function CanvasStateLabel({ text }: { text: string }) {
  return (
    <Text
      className="font-dm-sans"
      style={{
        fontSize: 11,
        fontWeight: "700",
        color: EMBER.colors.textFaint,
        textTransform: "uppercase",
        marginBottom: 2,
      }}
    >
      {text}
    </Text>
  );
}

function ComponentStatesPreview({
  surface,
  onAction,
}: {
  surface: ComponentSurface;
  onAction: (action: PlanAction) => void;
}) {
  return (
    <View style={{ gap: 22 }}>
      <View
        style={{
          ...(GLASS_CARD_STYLE as object),
          borderRadius: 12,
          paddingVertical: 12,
          paddingHorizontal: 14,
          gap: 4,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            fontWeight: "700",
            color: EMBER.colors.text,
          }}
        >
          {BEHAVIOR_LABELS[surface.behavior]}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 18,
            color: EMBER.colors.textMid,
          }}
        >
          {surface.rule}
        </Text>
      </View>

      {surface.filledRender ? (
        <View>
          <CanvasStateLabel text="Filled, fixture data" />
          {surface.filledRender()}
        </View>
      ) : surface.filled ? (
        <View>
          <CanvasStateLabel text="Filled, fixture data" />
          <ConceptSectionView
            section={{
              title: surface.label,
              subtitle: "",
              widgets: [surface.filled],
            }}
            onAction={onAction}
            topSpacing={6}
          />
        </View>
      ) : null}

      {surface.emptyRender ? (
        <View>
          <CanvasStateLabel text="Empty, current behavior" />
          {surface.emptyRender()}
        </View>
      ) : surface.behavior === "wire-empty" && surface.empty ? (
        <View>
          <CanvasStateLabel text="Empty state" />
          <ConceptSectionView
            section={{
              title: surface.label,
              subtitle: "",
              widgets: [surface.empty],
            }}
            onAction={onAction}
            topSpacing={6}
          />
        </View>
      ) : surface.behavior === "wire-hidden" ? (
        <View>
          <CanvasStateLabel text="When empty" />
          <HiddenWhenEmptyIndicator />
        </View>
      ) : null}

      {surface.note ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 18,
            color: EMBER.colors.textMid,
            paddingHorizontal: 2,
          }}
        >
          {surface.note}
        </Text>
      ) : null}
    </View>
  );
}
